/**
 * Shared anomaly presentation helpers.
 *
 * These were defined inside AnomaliesPage's component body, which meant the
 * anomaly detail view could not reuse them - so the two screens were free to
 * label the same anomaly differently. Now that the detail view is its own
 * route rather than a modal rendered by the list, one copy is the only way
 * they stay in agreement.
 */

import { Anomaly } from '../services/anomalyService';
import { BadgeVariant } from '../components/common/Badge';

export function formatCurrency(amount: number, currency: string = 'USD'): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
}

export function getSeverityVariant(severity: string): BadgeVariant {
  switch (severity) {
    case 'critical':
      return 'danger';
    case 'high':
      return 'warning';
    case 'medium':
      return 'info';
    default:
      return 'muted';
  }
}

/**
 * Anomaly type -> badge variant.
 *
 * Previously six bespoke `bg-<hue>-100 text-<hue>-800` literals covering
 * purple / amber / blue / green / indigo / gray - six saturated fills whose
 * only job was "these rows are different kinds". They now map onto the same
 * four semantic variants the rest of the app uses, so anomaly type stops
 * introducing three colour families nothing else in the product uses.
 */
export function getTypeVariant(type: string): BadgeVariant {
  switch (type) {
    case 'cost_spike':
      return 'danger';
    case 'cost_drift':
      return 'warning';
    case 'pattern_anomaly':
    case 'unusual_usage':
      return 'info';
    case 'new_service':
      return 'success';
    default:
      return 'muted';
  }
}

export function getStatusVariant(status: string): BadgeVariant {
  switch (status) {
    case 'resolved':
      return 'success';
    case 'acknowledged':
      return 'info';
    default:
      return 'muted';
  }
}

export function formatType(type: string): string {
  return type
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * FR-ANOM-007 layer 7 puts the bar for a confirmed alert at confidence > 0.7.
 * Most anomalies here come from detectors that never pass through that gate,
 * so the list was giving a single-detector 0.3-confidence finding exactly the
 * same visual weight as a fully-corroborated one.
 *
 * A null confidence means "detected before scoring existed" - not zero - so it
 * is deliberately left unlabelled rather than being called low-confidence.
 */
export const ALERT_CONFIDENCE_THRESHOLD = 0.7;

export function confidenceLabel(anomaly: Anomaly): { text: string; title: string } | null {
  const raw = anomaly.metadata?.confidence;
  if (typeof raw !== 'number') return null;
  if (raw > ALERT_CONFIDENCE_THRESHOLD) return null;
  return {
    text: `Informational · ${(raw * 100).toFixed(0)}%`,
    title: `Below the ${ALERT_CONFIDENCE_THRESHOLD * 100}% confidence bar for a confirmed alert - review this as a lead, not a verified problem. Open the detail page to see why confidence was capped.`,
  };
}

export function formatRelativeDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  return date.toLocaleDateString();
}
